import React, { useContext, useEffect, useRef, useState } from "react";
import { Route, useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import ErrorMessages from "../components/Notifications/ErrorMessages";
import SpinningLoader from "../components/Loaders/SpinningLoader";
import { AuthContext } from "../contexts/AuthContext";
import Login from "./Login";

const FullHeightContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AcesLoginRedirect = React.memo(function AcesLoginRedirect() {
  const { loginErrors, doAcesLogin, user, groupDataLoading, checkGroupErrors } =
    useContext(AuthContext);
  const [paramErrors, setParamErrors] = useState(null);
  // Read once at mount, before the URL is stripped below. While an exchange is
  // in flight, a user already in context is the previous session's, not the
  // one this auth code is for, so it must not trigger the redirect.
  const [authCode] = useState(() =>
    new URL(window.location).searchParams.get("authCode")
  );
  const [exchanging, setExchanging] = useState(Boolean(authCode));
  const exchangeStartedRef = useRef(false);
  const history = useHistory();

  useEffect(() => {
    if (exchangeStartedRef.current) {
      return;
    }

    if (!authCode) {
      if (!user) {
        setParamErrors([{ message: "Missing authentication parameters." }]);
      }
      return;
    }

    exchangeStartedRef.current = true;

    // Strip credentials from the URL so they don't sit in browser history,
    // address-bar autocomplete, or referer headers on subsequent requests.
    window.history.replaceState({}, "", window.location.pathname);

    // Use AuthContext's doAcesLogin for the full login flow
    // (token exchange, currentUserQuery, redirect-url support, sessionPhoto, workgroup check)
    doAcesLogin(authCode).finally(() => setExchanging(false));
  }, [authCode, doAcesLogin, user]);

  // Redirect logged-in users once group data is loaded
  useEffect(() => {
    if (user?.id && !groupDataLoading && !exchanging) {
      const redirectUrl = window.localStorage.getItem("redirect-url") || "/welcome";
      window.localStorage.removeItem("redirect-url");
      history.push(redirectUrl);
    }
  }, [user, groupDataLoading, exchanging, history]);

  const displayErrors = paramErrors || loginErrors || checkGroupErrors;

  // Show loading spinner while user is logged in (waiting for redirect)
  if (user?.id) {
    return (
      <FullHeightContainer>
        <SpinningLoader text="Logging In" />
      </FullHeightContainer>
    );
  }

  return (
    <Route
      render={(props) =>
        displayErrors ? (
          <FullHeightContainer>
            <Login {...props} />
            <ErrorMessages {...props} errors={displayErrors} />
          </FullHeightContainer>
        ) : (
          <FullHeightContainer>
            <SpinningLoader text="Logging In" />
          </FullHeightContainer>
        )
      }
    />
  );
});

export default AcesLoginRedirect;
